
/** CAVEATS:
 *  - If your DOM lib or runtime doesn't support `AbortSignal.reason`, the merged
 *    signal will still abort correctly, but `.reason` may be `undefined`.
 *    If you need to preserve a message on old platforms, maintain a fallback map
 *    keyed by requestId and consult it in your catch block.
 *
 * LIFECYCLE & CLEANUP:
 *  - The merged signal is backed by an internal controller. When any source
 *    aborts, listeners are removed from all sources and the map is cleared.
 *    You don't need to manually clean up.
 *
 * COMPATIBILITY:
 *  - Safe for ES5 downleveling and strict TypeScript DOM typings.
 *  - If you later upgrade TypeScript/DOM libs to support reasoned aborts,
 *    this implementation will automatically start forwarding reasons.
 * 
 * See PlanThunks.ts downloadPlanPdf usage for an example.
 */

export type ACWithReason = AbortController & { abort(reason?: any): void };
export const supportsAbortReason =
  typeof AbortSignal !== 'undefined' &&
  !!(AbortSignal.prototype as any) &&
  'reason' in (AbortSignal.prototype as any);

/** Abort a controller with an optional reason; falls back if not supported */
export function abortController(controller: AbortController, reason?: any)
{
	if (supportsAbortReason)
	{
		(controller as ACWithReason).abort(reason);
	}
	else
	{
		(controller as AbortController).abort(); // reason not supported at runtime
	}
}

/** Read a reason from a signal if available */
export function getSignalReason(signal?: AbortSignal): any | undefined
{
	return (signal as any)?.reason;
}

export function anySignal(signals: Array<AbortSignal | undefined>): AbortSignal
{
	const filtered: AbortSignal[] = [];
	for (let i = 0; i < signals.length; i++)
	{
		const s = signals[i];
		if (s)
		{
			filtered.push(s);
		}
	}
	if (filtered.length === 0)
	{
		// fresh controller; the caller can ignore this if they pass no signals
		return new AbortController().signal;
	}
	if (filtered.length === 1)
	{
		return filtered[0];
	}

	const controller = new AbortController();
	const handlers = new Map<AbortSignal, () => void>();

	function cleanup() 
	{
		handlers.forEach((h, sig) => sig.removeEventListener('abort', h));
		handlers.clear();
	}

	function adoptAbortFrom(s: AbortSignal)
	{
		if (!controller.signal.aborted)
		{
			const reason = getSignalReason(s) || new DOMException('Aborted', 'AbortError');
			abortController(controller, reason);
			cleanup();
		}
	}

	// If any is already aborted, adopt its reason immediately
	for (let i = 0; i < filtered.length; i++)
	{
		const s = filtered[i];
		if (s.aborted)
		{
			adoptAbortFrom(s);
			return controller.signal;
		}
	}

	// Wire listeners
	for (let i = 0; i < filtered.length; i++)
	{
		const s = filtered[i];
		const h = () => adoptAbortFrom(s);
		handlers.set(s, h);
		s.addEventListener('abort', h);
	}

	return controller.signal;
}